import './slider-block.scss'
import 'swiper/css';
import 'swiper/css/effect-fade';

import Swiper from 'swiper';
import { Navigation, Pagination, EffectFade } from 'swiper/modules';
import { SliderAutoplay, updateActiveTab, startLoaderAnimation, updateLoaderProgress } from '@/js/global.js';

const isWindows = navigator.platform.toUpperCase().includes('WIN');
if (isWindows) document.documentElement.classList.add('windows');

// Initialize all slider blocks on the page
function initSliderBlocks() {
    const sliderBlocks = document.querySelectorAll('.js-sliderBlock');
    
    sliderBlocks.forEach((sliderBlock, blockIndex) => {
        const slider = sliderBlock.querySelector('.js-slider');
        const navigation = sliderBlock.querySelector('.js-sliderNav');
        const navItems = navigation.querySelectorAll('li');
        const loaders = sliderBlock.querySelectorAll('.js-sliderLoader');
        const prevEl = sliderBlock.querySelector('.js-arrowPrev');
        const nextEl = sliderBlock.querySelector('.js-arrowNext');
        
        if (!slider || !navigation) return;
        
        let currentActiveIndex = 0;
        const totalSlides = navItems.length;
        
        // Create autoplay instance
        const autoplay = new SliderAutoplay();
        
        function handleSlideChange() {
            if (swiper.activeIndex < totalSlides - 1) {
                swiper.slideNext();
            } else {
                swiper.slideTo(0);
            }
        }
        
        function handleProgress(progressPercent) {
            updateLoaderProgress(loaders[currentActiveIndex], progressPercent);
        }
        
        function startAutoplayForSlide(slideIndex) {
            currentActiveIndex = slideIndex;
            startLoaderAnimation(loaders, slideIndex);
            autoplay.start(slideIndex, totalSlides, handleProgress, handleSlideChange);
        }
        
        /**
         * Scroll navigation to keep active item in view
         * Always positions active item as the first (leftmost) visible item
         * Shows 2 items on mobile, 4 items on desktop
         */
        function scrollToActiveItem(activeIndex) {
            if (!navItems[activeIndex]) return;
            
            const activeItem = navItems[activeIndex];
            const navContainer = navigation;
            
            // Special case: first item should always scroll to 0
            if (activeIndex === 0) {
                requestAnimationFrame(() => {
                    if (navContainer.scrollLeft > 2) {
                        if (navContainer.scrollTo) {
                            navContainer.scrollTo({
                                left: 0,
                                behavior: 'smooth'
                            });
                        } else {
                            navContainer.scrollLeft = 0;
                        }
                    }
                });
                return;
            }
            
            // Use requestAnimationFrame to ensure layout is updated before scrolling
            requestAnimationFrame(() => {
                // Calculate position relative to scroll container using getBoundingClientRect
                const containerRect = navContainer.getBoundingClientRect();
                const itemRect = activeItem.getBoundingClientRect();
                
                // Calculate the item's position relative to the container's content
                // This accounts for any padding, margins, or positioning
                const itemLeftRelative = itemRect.left - containerRect.left + navContainer.scrollLeft;
                const containerScrollLeft = navContainer.scrollLeft;
                
                // Target scroll position is the item's left position relative to container
                const targetScroll = itemLeftRelative;
                
                // Always scroll to ensure active item is at the start (with threshold to avoid jitter)
                if (Math.abs(containerScrollLeft - targetScroll) > 2) {
                    // Try scrollTo first, fallback to scrollLeft for better compatibility
                    if (navContainer.scrollTo) {
                        navContainer.scrollTo({
                            left: targetScroll,
                            behavior: 'smooth'
                        });
                    } else {
                        navContainer.scrollLeft = targetScroll;
                    }
                }
            });
        }
        
        // Configure Swiper navigation
        const swiperOptions = {
            modules: [Navigation, Pagination, EffectFade],
            effect: 'fade',
            fadeEffect: {
                crossFade: true
            },
            speed: 800,
            allowTouchMove: false,
            loop: false,
            on: {
                init: function() {
                    updateActiveTab(navItems, 0);
                    startAutoplayForSlide(0);
                    // Scroll to show first item after a brief delay to ensure layout is ready
                    setTimeout(() => {
                        scrollToActiveItem(0);
                    }, 100);
                },
                slideChange: function() {
                    const activeIndex = this.activeIndex;
                    updateActiveTab(navItems, activeIndex);
                    startAutoplayForSlide(activeIndex);
                    // Scroll navigation to keep active item in view
                    // Add a small delay to ensure DOM updates are complete
                    setTimeout(() => {
                        scrollToActiveItem(activeIndex);
                    }, 50);
                }
            }
        };

        // Create Swiper instance
        const swiper = new Swiper(slider, swiperOptions);
        
        // Add click handlers to navigation tabs
        navItems.forEach((navItem, index) => {
            navItem.addEventListener('click', () => {
                // Stop current autoplay
                autoplay.stop();
                
                // Change to the clicked slide
                swiper.slideTo(index);
                
                // Start autoplay for new slide
                startAutoplayForSlide(index);
                
                // Scroll navigation to keep clicked item in view
                scrollToActiveItem(index);
            });
        });

        // Add custom click handlers to arrow buttons for looping behavior
        // Override Swiper's default navigation to handle looping at boundaries
        if (prevEl) {
            prevEl.addEventListener('click', (e) => {
                // Stop current autoplay
                autoplay.stop();
                
                // Handle looping: if on first slide, go to last slide
                if (swiper.activeIndex === 0) {
                    swiper.slideTo(totalSlides - 1);
                } else {
                    swiper.slidePrev();
                }
            });
        }

        if (nextEl) {
            nextEl.addEventListener('click', (e) => {
                // Stop current autoplay
                autoplay.stop();
                
                // Handle looping: if on last slide, go to first slide
                if (swiper.activeIndex === totalSlides - 1) {
                    swiper.slideTo(0);
                } else {
                    swiper.slideNext();
                }
            });
        }
        
        // Add manual mouse enter/leave handling for pauseOnMouseEnter
        slider.addEventListener('mouseenter', () => {
            autoplay.pause();
        });
        
        slider.addEventListener('mouseleave', () => {
            autoplay.resume();
        });
    });
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', initSliderBlocks);